import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, CheckCircle2, Copy, Eye, EyeOff, Info, KeyRound, Loader2, Radio, RotateCw, Trash2, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/sonner';
import { integrationService } from '@/services';
import type { FreshdeskAgent, FreshdeskRotationResult, FreshdeskVerificationResult } from '@/services/integration.service';
import { useIntegrations } from '@/hooks';
import type { Integration } from '../types';
import {
	FRESHDESK_BOT_EMAIL as BOT_EMAIL,
	FRESHDESK_KIM_TAGS as KIM_TAGS,
	FRESHDESK_STEPS as STEPS,
	buildFreshdeskConnectPayload,
	buildFreshdeskRotationCancellation,
	canActivateFreshdeskRotation,
	canFinishFreshdeskSetup,
	getFreshdeskApiKeyReplacementErrorCopy,
	getFreshdeskVerificationErrorCopy,
	isFreshdeskRotationExpired,
	isValidFreshdeskSubdomain,
	reconcileFreshdeskActivation,
	resetFreshdeskRotationState,
	selectFreshdeskConnectedMetadata,
	stageFreshdeskRotationState
} from '../freshdesk-contract';

interface FreshdeskContentProps {
	integration: Integration;
	isOpen: boolean;
	onOpenChange?: (open: boolean) => void;
}

type ManageView = 'summary' | 'replace-key' | 'rotation' | 'disconnect';
type FreshdeskErrorData = { code?: string; response?: { status?: number; data?: { code?: string; error?: string; message?: string } } };

const getError = (error: unknown) => {
	const typedError = error as FreshdeskErrorData;
	const data = typedError?.response?.data;
	return { code: data?.code ?? typedError?.code ?? '', message: data?.error ?? data?.message ?? '', status: typedError?.response?.status };
};

const copyValue = async (value: string, label: string) => {
	try {
		await navigator.clipboard.writeText(value);
		toast.success(`${label} copied`);
	} catch {
		toast.error(`Could not copy ${label.toLowerCase()}`);
	}
};

const CopyField = ({ label, value, secret = false, multiline = false }: { label: string; value: string; secret?: boolean; multiline?: boolean }) => {
	const [shown, setShown] = useState(!secret);
	return (
		<div className='flex flex-col gap-1.5'>
			<Label className='text-xs text-muted-foreground'>{label}</Label>
			<div className={`flex gap-1.5 ${multiline ? 'items-start' : 'items-center'}`}>
				{multiline ? (
					<pre className='m-0 min-w-0 flex-1 whitespace-pre-wrap break-all rounded-md border bg-neutral-50 p-2 font-mono text-[11px] leading-4 text-neutral-800'>{shown ? value : '••••••••••••••••••••••••'}</pre>
				) : (
					<code className='min-w-0 flex-1 overflow-auto whitespace-nowrap rounded-md border bg-neutral-50 p-2 font-mono text-xs text-neutral-800'>{shown ? value : '••••••••••••••••••••••••'}</code>
				)}
				{secret && <Button type='button' size='icon-sm' variant='ghost' onClick={() => setShown((value) => !value)} title={shown ? 'Hide value' : 'Show value'}>{shown ? <EyeOff className='size-4' /> : <Eye className='size-4' />}</Button>}
				<Button type='button' size='icon-sm' variant='ghost' onClick={() => void copyValue(value, label)} title={`Copy ${label}`}><Copy className='size-4' /></Button>
			</div>
		</div>
	);
};

const Notice = ({ tone = 'info', title, children }: { tone?: 'info' | 'warning' | 'danger' | 'success'; title?: string; children: React.ReactNode }) => {
	const styles = tone === 'danger' ? 'border-red-200 bg-red-50 text-red-700' : tone === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-700' : tone === 'success' ? 'border-green-200 bg-green-50 text-green-700' : 'border-blue-200 bg-blue-50 text-blue-700';
	const Icon = tone === 'danger' || tone === 'warning' ? AlertTriangle : tone === 'success' ? CheckCircle2 : Info;
	return <div role={tone === 'danger' ? 'alert' : 'status'} aria-live={tone === 'danger' ? 'assertive' : 'polite'} aria-atomic='true' className={`flex gap-2 rounded-md border p-2.5 text-xs leading-5 ${styles}`}><Icon className='mt-0.5 size-4 shrink-0' /><div>{title && <p className='font-semibold'>{title}</p>}<div>{children}</div></div></div>;
};

const Stepper = ({ step, onStep }: { step: number; onStep: (step: number) => void }) => (
	<div className='flex items-start justify-between px-5'>
		{STEPS.map((label, index) => {
			const complete = index < step;
			return <div key={label} className='relative flex flex-1 flex-col items-center'>
				{index > 0 && <div className={`absolute right-1/2 top-3.5 h-px w-full ${index <= step ? 'bg-green-300' : 'bg-border'}`} />}
				<button type='button' disabled={index > step} aria-label={`Step ${index + 1}: ${label}${complete ? ' (completed)' : index === step ? ' (current)' : ' (not started)'}`} aria-current={index === step ? 'step' : undefined} onClick={() => index <= step && onStep(index)} className={`relative z-10 flex size-7 items-center justify-center rounded-full border text-[11px] ${complete ? 'border-green-300 bg-green-50 text-green-700' : index === step ? 'border-foreground bg-foreground text-background' : 'border-border bg-muted text-muted-foreground'}`}>{complete ? <><Check className='size-3.5' /><span className='sr-only'>Completed</span></> : index + 1}</button>
				<span className={`mt-1 text-[10px] ${index > step ? 'text-muted-foreground' : 'text-foreground'}`}>{label}</span>
			</div>;
		})}
	</div>
);

type FreshdeskDisplayAgent = Pick<FreshdeskAgent, 'id' | 'name' | 'email'>;
type FreshdeskRotationMetadata = Omit<FreshdeskRotationResult, 'webhook_token'>;

const formatAgent = (agent: FreshdeskDisplayAgent) => agent.name || agent.email || `Agent ${agent.id}`;

export const FreshdeskContent = ({ integration, isOpen, onOpenChange }: FreshdeskContentProps) => {
	const { refetch } = useIntegrations();
	// No webhook URLs are derived here any more: machine builds them from its own
	// WEBHOOK_INGEST_BASE_URL and points the three automation rules at them when it
	// provisions the rules, so the operator never copies an endpoint by hand.
	const [step, setStep] = useState(0);
	const [subdomain, setSubdomain] = useState('');
	const [apiKey, setApiKey] = useState('');
	const [verification, setVerification] = useState<FreshdeskVerificationResult | null>(null);
	const [verificationError, setVerificationError] = useState<{ title: string; body: string } | null>(null);
	const [isVerifying, setIsVerifying] = useState(false);
	const [isConnecting, setIsConnecting] = useState(false);
	const [isAbandonDialogOpen, setIsAbandonDialogOpen] = useState(false);

	const [manageView, setManageView] = useState<ManageView>('summary');
	const [newApiKey, setNewApiKey] = useState('');
	const [isReplacing, setIsReplacing] = useState(false);
	const [rotationState, setRotationState] = useState(() => resetFreshdeskRotationState<FreshdeskRotationMetadata>());
	const rotationSecretRef = useRef<string | null>(null);
	const [isStaging, setIsStaging] = useState(false);
	const [isActivating, setIsActivating] = useState(false);
	const [isCancellingRotation, setIsCancellingRotation] = useState(false);
	const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
	const [isDisconnecting, setIsDisconnecting] = useState(false);
	const { rotation } = rotationState;

	const resetRotationState = useCallback(() => {
		rotationSecretRef.current = null;
		setRotationState(resetFreshdeskRotationState<FreshdeskRotationMetadata>());
	}, []);

	const resetLocalState = useCallback(() => {
		setStep(0);
		setSubdomain('');
		setApiKey('');
		setVerification(null);
		setVerificationError(null);
		setIsVerifying(false);
		setIsConnecting(false);
		setIsAbandonDialogOpen(false);
		setManageView('summary');
		setNewApiKey('');
		setIsReplacing(false);
		resetRotationState();
		setIsStaging(false);
		setIsActivating(false);
		setIsCancellingRotation(false);
		setIsDiscardDialogOpen(false);
		setIsDisconnecting(false);
	}, [resetRotationState]);

	useEffect(() => {
		if (!isOpen) resetLocalState();
		return () => {
			rotationSecretRef.current = null;
		};
	}, [isOpen, resetLocalState]);

	const resetVerification = () => {
		setVerification(null);
		setVerificationError(null);
	};

	const navigateToStep = (nextStep: number) => {
		if (nextStep === 0 && step > 0 && verification) {
			setIsAbandonDialogOpen(true);
			return;
		}
		setStep(nextStep);
	};

	const confirmReturnToCredentials = () => {
		resetVerification();
		setStep(0);
		setIsAbandonDialogOpen(false);
	};

	const verify = async () => {
		if (!isValidFreshdeskSubdomain(subdomain)) {
			setVerificationError({ title: 'Enter a bare Freshdesk subdomain', body: 'Use only the lower-case part before .freshdesk.com, without a URL, dots, spaces, or slashes.' });
			return;
		}
		if (!apiKey) return;
		setIsVerifying(true);
		setVerificationError(null);
		try {
			const result = await integrationService.verifyFreshdeskIntegration(subdomain, apiKey);
			setVerification(result);
			setStep(1);
		} catch (error) {
			setVerificationError(getFreshdeskVerificationErrorCopy(getError(error).code));
		} finally {
			setIsVerifying(false);
		}
	};

	const finish = async () => {
		if (!verification) return;
		setIsConnecting(true);
		try {
			await integrationService.connectFreshdeskIntegration(buildFreshdeskConnectPayload(subdomain, apiKey, verification.webhook_token, verification.verification_receipt));
			await refetch();
			toast.success('Freshdesk connected');
			onOpenChange?.(false);
		} catch (error) {
			const { code, message } = getError(error);
			if (code === 'INVALID_VERIFICATION_RECEIPT' || code === 'VERIFIED_AGENT_CHANGED') {
				setStep(0);
				resetVerification();
				setVerificationError({ title: 'Verify the credentials again', body: code === 'VERIFIED_AGENT_CHANGED' ? 'The Freshdesk agent identity changed before Finish. Verify the intended dedicated agent again.' : 'The secure verification expired. No connection was saved.' });
			} else toast.error(message || 'Could not connect Freshdesk. Nothing was saved.');
		} finally {
			setIsConnecting(false);
		}
	};

	const replaceApiKey = async () => {
		if (!newApiKey) return;
		setIsReplacing(true);
		try {
			await integrationService.replaceFreshdeskApiKey(newApiKey);
			setNewApiKey('');
			setManageView('summary');
			await refetch();
			toast.success('Freshdesk API key replaced');
		} catch (error) {
			const { code, message } = getError(error);
			toast.error(getFreshdeskApiKeyReplacementErrorCopy(code, message));
		} finally {
			setIsReplacing(false);
		}
	};

	const openRotation = async () => {
		resetRotationState();
		setManageView('rotation');
		setIsStaging(true);
		try {
			const result = await integrationService.stageFreshdeskWebhookToken();
			rotationSecretRef.current = result.webhook_token;
			setRotationState(stageFreshdeskRotationState({ expires_at: result.expires_at, resumed: result.resumed }));
			if (result.resumed) toast.info('Resumed the pending webhook-token rotation');
		} catch (error) {
			setManageView('summary');
			toast.error(getError(error).message || 'Could not start webhook-token rotation');
		} finally {
			setIsStaging(false);
		}
	};

	const activateRotation = async () => {
		if (!rotation || !canActivateFreshdeskRotation(rotationState) || isFreshdeskRotationExpired(rotation.expires_at)) return;
		setIsActivating(true);
		try {
			await integrationService.activateFreshdeskWebhookToken();
			resetRotationState();
			setManageView('summary');
			await refetch();
			toast.success('Webhook token rotated');
		} catch (error) {
			const { code, message, status } = getError(error);
			if (code === 'PENDING_TOKEN_EXPIRED') {
				resetRotationState();
				setManageView('summary');
				toast.error('The pending token expired. Start a new rotation and update the rules again.');
			} else if (!status || status >= 500 || code === 'NO_PENDING_TOKEN') {
				try {
					const refreshed = await refetch();
					const freshdesk = refreshed.data?.find((candidate) => candidate.name.toLowerCase() === 'freshdesk');
					const outcome = reconcileFreshdeskActivation(freshdesk);
					if (outcome === 'resumable' && code !== 'NO_PENDING_TOKEN') {
						toast.error('Activation was not confirmed. The safe account state still shows a pending rotation; close and resume it before expiry.');
					} else {
						resetRotationState();
						setManageView('summary');
						toast.error(code === 'NO_PENDING_TOKEN' ? 'No pending token exists. Another tab may have activated, cancelled, or expired this rotation. Refresh integrations and verify every Freshdesk rule before making further changes.' : 'Activation status is indeterminate because the pending token is gone. Refresh integrations and verify every Freshdesk rule before making further changes.');
					}
				} catch {
					resetRotationState();
					setManageView('summary');
					toast.error('Activation status is indeterminate because the connection could not be refreshed. Refresh integrations before changing any Freshdesk rules.');
				}
			} else {
				toast.error(message || 'Webhook-token activation failed. Refresh integrations before retrying.');
			}
		} finally {
			setIsActivating(false);
		}
	};

	const discardRotation = async (confirmedDestructive = false) => {
		const cancellation = buildFreshdeskRotationCancellation(rotationState);
		if (cancellation.remoteRulesEdited && !confirmedDestructive) {
			setIsDiscardDialogOpen(true);
			return;
		}
		setIsDiscardDialogOpen(false);
		setIsCancellingRotation(true);
		try {
			await integrationService.cancelFreshdeskWebhookToken(cancellation.remoteRulesEdited, confirmedDestructive);
			resetRotationState();
			setManageView('summary');
			toast.success('Pending webhook token discarded');
		} catch (error) {
			toast.error(getError(error).message || 'Could not discard the pending token');
		} finally {
			setIsCancellingRotation(false);
		}
	};

	const closeAndResumeRotation = () => {
		resetLocalState();
		onOpenChange?.(false);
	};

	const disconnect = async () => {
		setIsDisconnecting(true);
		try {
			const result = await integrationService.disconnectFreshdeskIntegration();
			await refetch();
			toast.success(result.cleanup_guidance);
			onOpenChange?.(false);
		} catch (error) {
			toast.error(getError(error).message || 'Could not disconnect Freshdesk');
		} finally {
			setIsDisconnecting(false);
		}
	};

	if (integration.isConnected) {
		const connectedMetadata = selectFreshdeskConnectedMetadata(integration.authMetaData ?? {});
		const connectedSubdomain = connectedMetadata.subdomain;
		const agent: FreshdeskDisplayAgent = connectedMetadata.agent;
		const pendingExpiry = connectedMetadata.pendingExpiresAt;

		if (manageView === 'replace-key') return <div className='flex h-full flex-col px-5'>
			<div className='flex-1 space-y-4'><div><h3 className='text-sm font-semibold'>Replace API key</h3><p className='text-xs text-muted-foreground'>The current connection remains active until replacement succeeds.</p></div>
				<Notice>Replacing the API key preserves the current webhook token, so Kim’s automation rules keep authenticating unchanged.</Notice>
				<div className='space-y-2'><Label htmlFor='freshdesk-replacement-api-key'>New API key</Label><Input id='freshdesk-replacement-api-key' type='password' value={newApiKey} onChange={(event) => setNewApiKey(event.target.value)} placeholder='Enter the dedicated agent API key' /></div>
				<Notice tone='warning'>Freshdesk acceptance proves the agent identity only. Reply, note, and update permissions remain operation-specific. A failure leaves the old key and working connection unchanged.</Notice>
			</div>
			<div className='flex justify-end gap-2 py-4'><Button variant='outline' onClick={() => setManageView('summary')}>Cancel</Button><Button onClick={() => void replaceApiKey()} disabled={!newApiKey || isReplacing}>{isReplacing && <Loader2 className='size-4 animate-spin' />}Verify and replace</Button></div>
		</div>;

		if (manageView === 'rotation') return <div className='flex h-full flex-col px-5'>
			<div className='flex-1 space-y-4'><div><h3 className='text-sm font-semibold'>Rotate webhook token</h3><p className='text-xs text-muted-foreground'>Update Freshdesk before committing the new token.</p></div>
				{isStaging || !rotation ? <div className='flex items-center gap-2 text-xs text-muted-foreground'><Loader2 className='size-4 animate-spin' />Preparing a resumable token…</div> : <>
					<Notice tone='warning' title={rotation.resumed ? 'Pending rotation resumed' : 'New token · shown only in this rotation'}>This token remains valid until {new Date(rotation.expires_at).toLocaleString()}. The active token continues working until activation.</Notice>
					{isFreshdeskRotationExpired(rotation.expires_at) && <Notice tone='danger'>This pending token has expired. Do not activate it; return to management and start a new rotation.</Notice>}
					<CopyField label='New webhook token' value={rotationSecretRef.current ?? ''} secret />
					<p className='text-xs font-semibold'>Kim updates the rules for you</p>
					<p className='text-[11px] text-muted-foreground'>On activation Kim re-points all three Freshdesk automation rules at the new token in one pass. No manual rule editing — which is also why the three rules can no longer drift onto different tokens.</p>
					<Notice>Closing safely preserves the encrypted pending state and clears this displayed token. If activation cannot be confirmed, Kim refreshes safe connection state before reporting whether the rotation completed or remains resumable.</Notice>
				</>}
			</div>
			<div className='flex flex-wrap justify-end gap-2 py-4'><Button variant='ghost' className='mr-auto text-destructive' onClick={() => void discardRotation()} disabled={!rotation || isCancellingRotation}>{isCancellingRotation ? 'Discarding…' : 'Discard pending token'}</Button><Button variant='outline' onClick={closeAndResumeRotation}>Close and resume later</Button><Button onClick={() => void activateRotation()} disabled={!canActivateFreshdeskRotation(rotationState) || !rotation || isFreshdeskRotationExpired(rotation.expires_at) || isActivating}>{isActivating && <Loader2 className='size-4 animate-spin' />}Confirm and rotate</Button></div>
			<AlertDialog open={isDiscardDialogOpen} onOpenChange={setIsDiscardDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader><AlertDialogTitle>Discard pending webhook token?</AlertDialogTitle><AlertDialogDescription>Discarding now returns the integration to the current active token. Kim has not re-pointed any rule yet, so nothing stops authenticating — confirm to discard the pending token.</AlertDialogDescription></AlertDialogHeader>
					<AlertDialogFooter><AlertDialogCancel disabled={isCancellingRotation}>Keep pending token</AlertDialogCancel><AlertDialogAction onClick={() => void discardRotation(true)} disabled={isCancellingRotation}>Discard token</AlertDialogAction></AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>;

		if (manageView === 'disconnect') return <div className='flex h-full flex-col px-5'>
			<div className='flex-1 space-y-4'><div><h3 className='text-sm font-semibold'>Disconnect Freshdesk?</h3><p className='text-xs text-muted-foreground'>Kim will stop reading and updating Freshdesk tickets. Inbox and channel history are preserved.</p></div>
				<Notice tone='danger'>Disconnect immediately revokes webhook authentication and removes the automation rules Kim created.</Notice>
				<div className='rounded-lg border p-3 text-xs leading-6'><p className='font-semibold'>Kim removes on disconnect:</p><ul className='list-disc pl-5'><li>The three “Kim AI - …” automation rules it created</li></ul><p className='font-semibold'>You may still want to remove:</p><ul className='list-disc pl-5'><li>The dedicated Kim agent, if no longer needed</li><li>Any saved views you built on Kim’s tags</li></ul><p className='mt-2 text-muted-foreground'>Rules the team wrote are never touched. If Freshdesk is unreachable, disconnect still completes and reports what was left behind. Inbox and channel history are preserved.</p></div>
				<Notice tone='warning'>Until removed, those automations may continue sending requests that Kim rejects.</Notice>
			</div>
			<div className='flex justify-end gap-2 py-4'><Button variant='outline' onClick={() => setManageView('summary')}>Cancel</Button><Button variant='destructive' onClick={() => void disconnect()} disabled={isDisconnecting}>{isDisconnecting && <Loader2 className='size-4 animate-spin' />}Disconnect</Button></div>
		</div>;

		return <div className='flex flex-col gap-5 px-5 pb-8'>
			<div><h3 className='text-sm font-semibold'>Connected Freshdesk</h3><p className='text-xs text-muted-foreground'>Your Freshdesk workspace is linked to Kim.</p></div>
			<div className='grid grid-cols-2 gap-3 rounded-lg border p-3 text-xs'><div><p className='text-muted-foreground'>Connected workspace</p><p className='font-medium'>{connectedSubdomain}.freshdesk.com</p></div><div><p className='text-muted-foreground'>Reply agent</p><p className='font-medium'>{formatAgent(agent)}</p>{agent.email && <p className='text-muted-foreground'>{agent.email}</p>}</div><div><p className='text-muted-foreground'>API key</p><p className='font-medium'>Stored securely · Not viewable</p></div><div><p className='text-muted-foreground'>Automation rules</p><p className='font-medium'>3 rules · managed by Kim</p></div></div>
			{pendingExpiry && <Notice tone='warning'>A webhook-token rotation is pending until {new Date(pendingExpiry).toLocaleString()}. Continue rotation to retrieve the resumable pending token.</Notice>}
			<Notice>Credentials were accepted through Freshdesk agents/me. Operation-specific permissions are checked when each operation runs.</Notice>
			<div className='grid grid-cols-2 gap-2'><Button variant='outline' onClick={() => setManageView('replace-key')}><KeyRound className='size-4' />Replace API key</Button><Button variant='outline' onClick={() => void openRotation()}><RotateCw className='size-4' />{pendingExpiry ? 'Continue token rotation' : 'Rotate webhook token'}</Button></div>
			<Button variant='ghost' className='self-start text-destructive hover:text-destructive' onClick={() => setManageView('disconnect')}><Trash2 className='size-4' />Disconnect Freshdesk</Button>
		</div>;
	}

	const content = step === 0 ? <div className='space-y-4'>
		<div><h3 className='text-sm font-semibold'>Connect your Freshdesk account</h3><p className='text-xs text-muted-foreground'>Use credentials from a dedicated Freshdesk agent. Kim uses agents/me to confirm the credential and identify its agent. Nothing is saved until Finish.</p></div>
		<Notice><strong>Use a dedicated Kim agent.</strong> Give it the ticket read, reply, note, and update permissions your workflows need. Verification does not prove those operation-specific capabilities.</Notice>
		<div className='space-y-2 rounded-lg border p-3'><div><p className='text-xs font-semibold'>Agent email</p><p className='text-[11px] text-muted-foreground'>Create the agent in Freshdesk with this address, then paste its API key below. Freshdesk emails the activation invite here. Kim replies as whichever agent owns the key you paste — use this dedicated agent's key, not your own, or Kim's replies will appear to come from you.</p></div><CopyField label='Use this address' value={BOT_EMAIL} /></div>
		<div className='space-y-2'><Label htmlFor='freshdesk-subdomain'>Freshdesk subdomain</Label><div className='flex items-center'><Input id='freshdesk-subdomain' value={subdomain} onChange={(event) => { setSubdomain(event.target.value); resetVerification(); }} placeholder='acme-support' className='rounded-r-none' /><span className='h-9 rounded-r-md border border-l-0 bg-muted px-3 py-2 text-xs text-muted-foreground'>.freshdesk.com</span></div><p className='text-[11px] text-muted-foreground'>Enter only the lower-case part before .freshdesk.com.</p></div>
		<div className='space-y-2'><Label htmlFor='freshdesk-api-key'>API key</Label><Input id='freshdesk-api-key' type='password' value={apiKey} onChange={(event) => { setApiKey(event.target.value); resetVerification(); }} placeholder='Enter the dedicated agent API key' /><p className='text-[11px] text-muted-foreground'>Found in the agent’s Profile settings. The key is never shown again after connection.</p></div>
		{verificationError && <Notice tone='danger' title={verificationError.title}>{verificationError.body}</Notice>}
	</div> : step === 1 && verification ? <div className='space-y-4'>
		<div><h3 className='text-sm font-semibold'>Agent identified</h3><p className='text-xs text-muted-foreground'>Freshdesk accepted the credentials and returned this agent identity. Operation-specific permissions are checked when each action runs.</p></div>
		<div className='flex items-center gap-3 rounded-lg border p-4'><div className='flex size-10 items-center justify-center rounded-full bg-green-50 text-sm font-semibold text-green-700'>{(verification.agent.name || verification.agent.email || 'FD').slice(0, 2).toUpperCase()}</div><div className='min-w-0 flex-1'><p className='text-sm font-semibold'>{formatAgent(verification.agent)}</p><p className='truncate text-xs text-muted-foreground'>{verification.agent.email || 'Email not returned'} · {subdomain}.freshdesk.com</p></div><span className='flex items-center gap-1 text-xs text-green-700'><UserCheck className='size-4' />Verified</span></div>
		<div className='space-y-2 text-xs'><p className='flex items-center gap-2'><Check className='size-4 text-green-600' />API key accepted for {subdomain}</p><p className='flex items-center gap-2'><Check className='size-4 text-green-600' />Reply identity: {formatAgent(verification.agent)}</p></div>
		<Notice tone='warning'>Read, reply, note, update, and plan-specific capabilities are not implied by this identity check. Freshdesk validates them when those operations run.</Notice>
	</div> : step === 2 ? <div className='space-y-4'>
		<div><h3 className='text-sm font-semibold'>What Kim does to your tickets</h3><p className='text-xs text-muted-foreground'>Reference only — nothing here needs configuring. Kim applies these tags as it works.</p></div>
		{KIM_TAGS.map((tag) => <div key={tag.name} className='flex flex-col gap-1 rounded-lg border p-3'>
			<code className='w-fit rounded-md border bg-neutral-50 px-2 py-1 font-mono text-xs text-neutral-800'>{tag.name}</code>
			<p className='text-[11px]'><span className='text-muted-foreground'>Applied: </span>{tag.applied}</p>
			<p className='text-[11px]'><span className='text-muted-foreground'>Useful for: </span>{tag.why}</p>
		</div>)}
		<Notice>Freshdesk has no tag registry, so these are created the first time Kim applies them. To build an agent view later, filter tickets on <strong>Tags is in</strong> the tag above — the tag becomes selectable once it has been used at least once.</Notice>
	</div> : <div className='space-y-4 text-center'>
		<div className='mx-auto flex size-12 items-center justify-center rounded-full bg-muted'><Radio className='size-6' /></div><div><h3 className='text-sm font-semibold'>Ready to connect</h3><p className='text-xs text-muted-foreground'>Finishing stores the API key and creates Kim’s automation rules in Freshdesk. Nothing is saved until then.</p></div>
		<div className='grid grid-cols-2 gap-2 rounded-lg border p-3 text-left text-xs'><span className='text-muted-foreground'>Workspace</span><span>{subdomain}.freshdesk.com</span><span className='text-muted-foreground'>Credentials</span><span>Accepted by Freshdesk</span><span className='text-muted-foreground'>Reply agent</span><span>{verification ? formatAgent(verification.agent) : '—'}</span><span className='text-muted-foreground'>Automation rules</span><span>Created on Finish</span><span className='text-muted-foreground'>Operation permissions</span><span>Checked at runtime</span><span className='text-muted-foreground'>Credential storage</span><span>On Finish only</span></div>
		<Notice tone='warning'>Creating automation rules is an administrator action. If the connected agent lacks that role, Finish will fail and nothing is saved.</Notice>
	</div>;

	const canNext = step === 0 ? Boolean(apiKey && subdomain) : step === 1 ? Boolean(verification) : step === 2;
	const canFinish = canFinishFreshdeskSetup(Boolean(verification));
	return <div className='flex h-full flex-col'>
		<Stepper step={step} onStep={navigateToStep} /><Separator className='mt-3' />
		<div className='flex-1 overflow-auto px-5 py-4'>{content}</div>
		<div className='flex items-center justify-between px-5 pb-5 pt-4'><span className='text-[10px] text-muted-foreground'>Not saved until Finish</span><div className='flex gap-2'><Button variant='outline' disabled={step === 0 || isVerifying || isConnecting} onClick={() => navigateToStep(Math.max(0, step - 1))}>Back</Button>{step === STEPS.length - 1 ? <Button onClick={() => void finish()} disabled={!canFinish || isConnecting}>{isConnecting && <Loader2 className='size-4 animate-spin' />}Finish</Button> : <Button onClick={() => step === 0 ? void verify() : setStep((value) => Math.min(STEPS.length - 1, value + 1))} disabled={!canNext || isVerifying}>{isVerifying && <Loader2 className='size-4 animate-spin' />}{step === 0 ? 'Verify and continue' : 'Next'}</Button>}</div></div>
		<AlertDialog open={isAbandonDialogOpen} onOpenChange={setIsAbandonDialogOpen}>
			<AlertDialogContent>
				<AlertDialogHeader><AlertDialogTitle>Return to credentials?</AlertDialogTitle><AlertDialogDescription>Returning to Credentials invalidates this signed verification receipt and webhook token. You will need to verify again; Kim provisions the Freshdesk rules with the newly issued token at Finish.</AlertDialogDescription></AlertDialogHeader>
				<AlertDialogFooter><AlertDialogCancel>Keep verified setup</AlertDialogCancel><AlertDialogAction onClick={confirmReturnToCredentials}>Reset verified setup</AlertDialogAction></AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	</div>;
};
